import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Checkbox,
  Link,
} from '@chakra-ui/react'
import { LuScale, LuFileText } from 'react-icons/lu'
import { useEffect, useState } from 'react'
import { useUiPreferences, useUpdateUiPreferences } from '../../api/api_hooks/user/uiPreferences'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'
import {
  LEGAL_VERSION,
  LEGAL_DOCS,
  hasAcceptedLegal,
  hasAcceptedLegalLocal,
  recordLegalAcceptanceLocal,
} from '../../config/legal'

/**
 * Blocking consent gate for the CURRENT legal version (CGU / CGV / politique
 * de confidentialité). Mirrors AIActNotice:
 *   - new users accept via the required checkbox at sign-up (registerForm),
 *     which writes the localStorage fast-path so this modal stays silent and
 *     then persists their acceptance server-side on first authenticated load;
 *   - existing users, or anyone whose accepted version is older than the
 *     current LEGAL_VERSION, are re-prompted here.
 *
 * Acceptance is stored on the BE (uiPreferences.legalAcceptedAt /
 * legalAcceptedVersion) so it is provable and sticks across devices.
 */
export default function LegalConsentNotice() {
  const [isOpen, setIsOpen] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const { data: prefs, isLoading: prefsLoading } = useUiPreferences()
  const { mutateAsync: updatePrefs } = useUpdateUiPreferences()
  const acceptedOnServer = hasAcceptedLegal(prefs)

  useEffect(() => {
    if (prefsLoading) return
    if (acceptedOnServer) return

    // Accepted on this device (e.g. just now at sign-up) but not yet mirrored
    // to the server: persist silently, no modal.
    if (hasAcceptedLegalLocal()) {
      updatePrefs({
        legalAcceptedAt: new Date().toISOString(),
        legalAcceptedVersion: LEGAL_VERSION,
      }).catch(() => { /* best-effort */ })
      return
    }

    const t = window.setTimeout(() => setIsOpen(true), 300)
    return () => window.clearTimeout(t)
  }, [prefsLoading, acceptedOnServer, updatePrefs])

  const accept = async () => {
    recordLegalAcceptanceLocal()
    setIsOpen(false)
    try {
      await updatePrefs({
        legalAcceptedAt: new Date().toISOString(),
        legalAcceptedVersion: LEGAL_VERSION,
      })
    } catch {
      // localStorage already absorbed the acceptance; server sync retries next load.
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered size="xl" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.700" />
      <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
        <ModalHeader
          bgGradient={CHROME_HEADER_GRADIENT}
          color="white"
          py={5}
          px={7}
        >
          <HStack spacing={4}>
            <Box w="46px" h="46px" borderRadius="xl" bg="whiteAlpha.200" display="grid" sx={{ placeItems: 'center' }}>
              <Icon as={LuScale} boxSize={6} />
            </Box>
            <VStack align="start" spacing={0.5}>
              <Text fontSize="lg" fontWeight="800" letterSpacing="tight">
                Mise à jour de nos conditions
              </Text>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">
                Merci de prendre connaissance de nos documents légaux pour continuer
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalBody py={6} px={7}>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.700" lineHeight="1.65">
              Pour continuer à utiliser Careflai, veuillez prendre connaissance de nos conditions
              d'utilisation, conditions de vente et de notre politique de confidentialité.
              Cette mise à jour renforce notamment l'engagement de confidentialité
              réciproque (CGV, article 9) : les résultats d'évaluation et mesures de
              performance ne peuvent être communiqués à des tiers — industriels ou
              concurrents — ni servir à un produit concurrent sans accord écrit des
              deux parties, pendant le contrat et cinq ans au-delà.
            </Text>

            <VStack align="stretch" spacing={2}>
              {LEGAL_DOCS.filter((d) => d.slug !== 'mentions-legales').map((d) => (
                <Link
                  key={d.slug}
                  href={`/legal/${d.slug}`}
                  target="_blank"
                  _hover={{ textDecoration: 'none', bg: 'purple.50' }}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  px={4}
                  py={3}
                >
                  <HStack spacing={3}>
                    <Icon as={LuFileText} color="purple.500" boxSize={4} />
                    <Text fontSize="sm" fontWeight="600" color="gray.800">{d.label}</Text>
                    <Text fontSize="2xs" color="gray.400" ml="auto">Ouvrir ↗</Text>
                  </HStack>
                </Link>
              ))}
            </VStack>

            <Checkbox
              isChecked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              colorScheme="purple"
              size="sm"
              alignItems="flex-start"
              spacing={3}
              mt={1}
            >
              <Text fontSize="xs" color="gray.700" lineHeight="1.55">
                J'ai lu et j'accepte les conditions générales d'utilisation, les conditions générales
                de vente et la politique de confidentialité de Careflai.
              </Text>
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" py={4} px={7}>
          <Button
            colorScheme="purple"
            onClick={accept}
            isDisabled={!confirmed}
            size="md"
            borderRadius="lg"
            fontWeight="600"
          >
            Accepter et continuer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
